import React, { useRef, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Loader2, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { MESSAGE_VARIABLES } from "@/lib/messageVariables";
import { deleteFluxoMediaByUrl, uploadFluxoMedia } from "@/lib/fluxoMedia";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { FluxoBlock } from "@/hooks/useFluxos";
import { BLOCK_META } from "./blockMeta";

export interface AssistantOption {
  id: string;
  name: string;
}

interface BlockCardProps {
  block: FluxoBlock;
  assistants: AssistantOption[];
  accountId?: string | null;
  fluxoId?: string | null;
  onChange: (block: FluxoBlock) => void;
  onRemove: (id: string) => void;
}

const MAX_IMAGE_SIZE_MB = 2;
const MAX_AUDIO_SIZE_MB = 15;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png"];
const ALLOWED_AUDIO_TYPES = ["audio/mpeg", "audio/mp3"];

const FLUXO_VARIABLES = MESSAGE_VARIABLES.filter((v) => v.token !== "{{ jid }}");

interface FileUploadFieldProps {
  accept: string;
  label: string;
  fileName?: string;
  hasFile: boolean;
  maxSizeMb: number;
  allowedTypes?: string[];
  invalidTypeMessage?: string;
  uploading?: boolean;
  disabled?: boolean;
  onFile: (file: File) => void;
  onClear: () => void;
}

const FileUploadField: React.FC<FileUploadFieldProps> = ({
  accept,
  label,
  fileName,
  hasFile,
  maxSizeMb,
  allowedTypes,
  invalidTypeMessage,
  uploading = false,
  disabled = false,
  onFile,
  onClear,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (allowedTypes && !allowedTypes.includes(file.type)) {
      toast.error(invalidTypeMessage || "Formato de arquivo inválido.");
      return;
    }
    if (file.size > maxSizeMb * 1024 * 1024) {
      toast.error(`Arquivo muito grande. O limite é ${maxSizeMb}MB.`);
      return;
    }
    onFile(file);
  };

  return (
    <div className="flex items-center gap-2">
      <label
        className={`flex flex-1 items-center gap-2 px-3 py-2 rounded-md border border-dashed border-[#CBD5E1] bg-[#F8FAFC] min-w-0 dark:border-neutral-600 dark:bg-neutral-700/50 ${
          disabled || uploading
            ? "opacity-60 cursor-not-allowed"
            : "cursor-pointer hover:border-[#94A3B8] dark:hover:border-neutral-500"
        } transition-colors`}
      >
        {uploading ? (
          <Loader2 className="w-4 h-4 text-[#94A3B8] shrink-0 animate-spin dark:text-neutral-400" />
        ) : (
          <Upload className="w-4 h-4 text-[#94A3B8] shrink-0 dark:text-neutral-400" />
        )}
        <span className="text-sm text-[#64748B] truncate dark:text-neutral-400">
          {uploading ? "Enviando arquivo..." : hasFile ? fileName || "Arquivo selecionado" : label}
        </span>
        <input
          type="file"
          className="hidden"
          accept={accept}
          disabled={disabled || uploading}
          onChange={handleChange}
        />
      </label>
      {hasFile && !uploading && (
        <button
          onClick={onClear}
          className="w-[34px] h-[34px] shrink-0 flex justify-center items-center rounded-[10px] border border-border text-[#64748B] hover:bg-[#F1F5F9] dark:rounded-[8px] dark:border-[#3F3F46] dark:text-neutral-400 dark:hover:bg-[#27272A]"
          aria-label="Remover arquivo"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
};

const BlockCard: React.FC<BlockCardProps> = ({
  block,
  assistants,
  accountId,
  fluxoId,
  onChange,
  onRemove,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: block.id });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [uploading, setUploading] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const insertVariableAtCursor = (token: string) => {
    if (block.type !== "texto") return;
    const el = textareaRef.current;
    if (!el) {
      onChange({ ...block, content: block.content + token });
      return;
    }
    const start = el.selectionStart ?? block.content.length;
    const end = el.selectionEnd ?? block.content.length;
    const next = block.content.slice(0, start) + token + block.content.slice(end);
    onChange({ ...block, content: next });
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const handleMediaUpload = async (file: File, kind: "imagem" | "audio") => {
    if (!accountId || !fluxoId) {
      toast.error("Não foi possível identificar a conta para enviar o arquivo.");
      return;
    }

    setUploading(true);
    try {
      if (block.type === "imagem" || block.type === "audio") {
        await deleteFluxoMediaByUrl(block.url);
      }

      const publicUrl = await uploadFluxoMedia({
        accountId,
        fluxoId,
        blockId: block.id,
        file,
        kind,
        fileName: file.name,
      });

      if (kind === "imagem" && block.type === "imagem") {
        onChange({ ...block, url: publicUrl, fileName: file.name });
      } else if (kind === "audio" && block.type === "audio") {
        onChange({ ...block, url: publicUrl, fileName: file.name });
      }
    } catch (err) {
      console.error("💥 [BlockCard] Upload falhou:", err);
      toast.error(err instanceof Error ? err.message : "Erro ao enviar arquivo");
    } finally {
      setUploading(false);
    }
  };

  const handleClearMedia = async () => {
    if (block.type !== "imagem" && block.type !== "audio") return;
    await deleteFluxoMediaByUrl(block.url);
    onChange({ ...block, url: "", fileName: undefined });
  };

  const renderContent = () => {
    switch (block.type) {
      case "texto":
        return (
          <div className="flex flex-col gap-2">
            <Textarea
              ref={textareaRef}
              value={block.content}
              onChange={(e) => onChange({ ...block, content: e.target.value })}
              placeholder="Digite a mensagem de texto..."
              className="min-h-[80px]"
            />
            <div className="flex flex-col gap-1.5">
              <span className="text-xs text-[#64748B] dark:text-neutral-300">Variáveis disponíveis</span>
              <div className="flex flex-wrap gap-1.5">
                {FLUXO_VARIABLES.map((variable) => (
                  <button
                    key={variable.token}
                    type="button"
                    onClick={() => insertVariableAtCursor(variable.token)}
                    title={variable.description}
                    className="text-[11px] font-mono px-2 py-1 rounded bg-[#F1F5F9] text-[#334155] border border-border hover:bg-[#E2E8F0] transition-colors dark:bg-neutral-700/70 dark:text-neutral-200 dark:border-[#3F3F46] dark:hover:bg-neutral-600"
                  >
                    {variable.token}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      case "imagem":
        return (
          <div className="flex flex-col gap-2">
            <FileUploadField
              accept="image/jpeg,image/png,.jpg,.jpeg,.png"
              label="Escolher imagem..."
              fileName={block.fileName}
              hasFile={Boolean(block.url)}
              maxSizeMb={MAX_IMAGE_SIZE_MB}
              allowedTypes={ALLOWED_IMAGE_TYPES}
              invalidTypeMessage="Formato inválido. Use jpeg, jpg ou png."
              uploading={uploading}
              disabled={!accountId || !fluxoId}
              onFile={(file) => void handleMediaUpload(file, "imagem")}
              onClear={() => void handleClearMedia()}
            />
            {block.url && (
              <img
                src={block.url}
                alt="Pré-visualização da imagem"
                className="max-h-[160px] w-fit rounded-[8px] border border-border object-contain dark:border-[#3F3F46]"
              />
            )}
            <p className="text-xs text-[#64748B] dark:text-neutral-400">
              Tamanho máximo de 2MB. Arquivos aceitos jpeg, jpg, png.
            </p>
          </div>
        );
      case "audio":
        return (
          <div className="flex flex-col gap-2">
            <FileUploadField
              accept="audio/mpeg,.mp3"
              label="Escolher áudio..."
              fileName={block.fileName}
              hasFile={Boolean(block.url)}
              maxSizeMb={MAX_AUDIO_SIZE_MB}
              allowedTypes={ALLOWED_AUDIO_TYPES}
              invalidTypeMessage="Formato inválido. Use .mp3."
              uploading={uploading}
              disabled={!accountId || !fluxoId}
              onFile={(file) => void handleMediaUpload(file, "audio")}
              onClear={() => void handleClearMedia()}
            />
            {block.url && (
              <audio controls src={block.url} className="w-full max-w-[400px] h-[36px]" />
            )}
            <p className="text-xs text-[#64748B] dark:text-neutral-400">
              Tamanho máximo de 15MB. Arquivo aceito .mp3
            </p>
          </div>
        );
      case "delay":
        return (
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={1}
              value={block.seconds}
              onChange={(e) =>
                onChange({ ...block, seconds: Math.max(1, Number(e.target.value) || 1) })
              }
              className="w-[120px]"
            />
            <span className="text-sm text-[#64748B] dark:text-neutral-400">segundos de espera</span>
          </div>
        );
      case "assistente":
        return (
          <div className="w-full min-w-0 max-w-full">
            <Select
              value={block.assistantId || undefined}
              onValueChange={(value) => onChange({ ...block, assistantId: value })}
            >
              <SelectTrigger className="bg-white border-border text-[#0F172A] w-full max-w-full min-w-0 dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-neutral-50 [&>span]:min-w-0 [&>span]:truncate">
                <SelectValue placeholder="Selecione um assistente" />
              </SelectTrigger>
              <SelectContent className="bg-white border-border dark:bg-[#18181B] dark:border-[#3F3F46] max-w-[min(100vw-2rem,360px)]">
                {assistants.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-[#64748B] dark:text-neutral-400">
                    Nenhum assistente disponível
                  </div>
                ) : (
                  assistants.map((assistant) => (
                    <SelectItem key={assistant.id} value={assistant.id} className="max-w-full">
                      <span className="block truncate">{assistant.name}</span>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        );
    }
  };

  const meta = BLOCK_META[block.type];

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="rounded-[12px] border border-border bg-[#F8FAFC] p-4 min-w-0 overflow-hidden dark:border-[#3F3F46] dark:bg-[#18181B]"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[#0F172A] dark:text-neutral-50">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing touch-none text-[#94A3B8] hover:text-[#64748B] dark:text-neutral-500 dark:hover:text-neutral-300"
            aria-label="Arrastar para reordenar"
          >
            <GripVertical size={18} />
          </button>
          {meta.icon}
          <span className="text-sm font-semibold">{meta.label}</span>
        </div>
        <button
          onClick={() => onRemove(block.id)}
          className="w-[30px] h-[30px] flex justify-center items-center rounded-[8px] text-red-500 hover:bg-red-50 dark:text-red-400 dark:hover:bg-[#27272A]"
          aria-label="Remover bloco"
        >
          <Trash2 size={16} />
        </button>
      </div>
      {renderContent()}
    </div>
  );
};

export default BlockCard;
